/**
 * Copyright © 2025 Delicious AI, LLC
 */

import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  getProductPopperModifiers,
  getProductPopperPlacement,
  productPopoverContentSx,
  productPopperProps,
  StyledPopper,
  useProductPopover,
} from '@/components/bounding-box/use-product-popover'
import ProductDetailsCard, {
  ProductInitialInfo,
} from '@/components/ProductDetailsCard'
import { ProductFragment } from '@/graphql'
import useT from '@/i18n/useT'
import { getVolumeOz } from '@/pages/coolers/utils/planogram-image-fit'

const VOLUME_SCALE_POWER = 0.4
const DEFAULT_SHELF_HEIGHT = 80
const GAP_PX = 4
const FALLBACK_ASPECT_RATIO = 0.6

const RenderedPlanogram = ({
  products,
  reversed = false,
  highlightByUpc,
  highlightByPosition,
  facingTitleByPosition,
  renderPopoverContent,
  popoverEnabledByPosition,
  onFacingHover,
  onFacingLeave,
  loadingByUpc,
  imageStyleByUpc,
  imageFit = 'contain',
  maxHeight,
  shelfHeight = DEFAULT_SHELF_HEIGHT,
  productInfoByUpc,
}: {
  products: (Pick<
    ProductFragment,
    'upcA' | 'planogramImageUrl' | 'marketingImageUrl'
  > & {
    preferredName?: ProductFragment['preferredName'] | null
    packaging?: { size?: number | null; unit?: string | null } | null
    missing?: boolean
  })[][]
  reversed?: boolean
  highlightByUpc?: Record<string, { bgColor?: string; borderColor?: string }>
  highlightByPosition?: Record<
    string,
    { bgColor?: string; borderColor?: string }
  >
  facingTitleByPosition?: Record<string, string>
  renderPopoverContent?: (upc: string, positionKey?: string) => React.ReactNode
  popoverEnabledByPosition?: Record<string, boolean>
  onFacingHover?: (upc: string) => void
  onFacingLeave?: () => void
  loadingByUpc?: Record<string, boolean>
  imageStyleByUpc?: Record<
    string,
    {
      scale?: number
      scaleX?: number
      scaleY?: number
      objectPosition?: string
    }
  >
  imageFit?: 'contain' | 'cover'
  maxHeight?: number
  shelfHeight?: number
  productInfoByUpc?: Record<string, ProductInitialInfo>
}) => {
  const theme = useTheme()
  const { t } = useT()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    open,
    anchorEl,
    arrowRef,
    handleBoxClick,
    handleBoxMouseEnter,
    handleBoxMouseLeave,
    handlePopperMouseEnter,
    handlePopperMouseLeave,
    activeUpc,
    styles,
    setArrowRef,
  } = useProductPopover()
  const [mobileActiveUpc, setMobileActiveUpc] = useState<string | null>(null)
  const [activePositionKey, setActivePositionKey] = useState<string>()
  const [mobileActivePositionKey, setMobileActivePositionKey] =
    useState<string>()
  const [naturalDims, setNaturalDims] = useState<
    Record<string, { w: number; h: number }>
  >({})
  const availableWidthRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState<number>(0)

  useEffect(() => {
    if (!availableWidthRef.current) return
    const el = availableWidthRef.current
    const observer = new ResizeObserver(entries =>
      setContainerWidth(entries[0].contentRect.width),
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const preferredNameByUpc = useMemo(
    () =>
      Object.fromEntries(
        products
          .flat()
          .filter(facing => facing.preferredName)
          .map(facing => [facing.upcA, facing.preferredName as string]),
      ),
    [products],
  )

  const popperPlacement = useMemo(
    () => getProductPopperPlacement(anchorEl),
    [anchorEl],
  )

  const drawerContent = useMemo(() => {
    if (!mobileActiveUpc) return null
    if (renderPopoverContent)
      return renderPopoverContent(mobileActiveUpc, mobileActivePositionKey)

    return (
      <ProductDetailsCard
        upc={mobileActiveUpc}
        hideDetails
        preferredNameOverride={preferredNameByUpc[mobileActiveUpc]}
        initialInfo={productInfoByUpc?.[mobileActiveUpc]}
      />
    )
  }, [
    mobileActiveUpc,
    mobileActivePositionKey,
    preferredNameByUpc,
    productInfoByUpc,
    renderPopoverContent,
  ])

  const globalMaxVol = useMemo(() => {
    const vols = products
      .flat()
      .map(f => getVolumeOz(f.packaging?.size, f.packaging?.unit))
      .filter((v): v is number => v !== null)
    if (vols.length === 0) return null
    return Math.max(...vols)
  }, [products])

  const spacingPx = useMemo(
    () => ({
      outerPadding: Number(theme.spacing(3).replace('px', '')),
      innerPadding: Number(theme.spacing(1).replace('px', '')),
      shelfGap: Number(theme.spacing(2).replace('px', '')),
      shelfLine: 4,
      frameRadius: 4,
    }),
    [theme],
  )

  const getBaseProductHeight = useCallback(
    (vol: number | null) =>
      globalMaxVol && vol !== null
        ? Math.round(
            shelfHeight * Math.pow(vol / globalMaxVol, VOLUME_SCALE_POWER),
          )
        : shelfHeight,
    [globalMaxVol, shelfHeight],
  )

  const getBaseProductWidth = useCallback(
    (upc: string, productHeight: number) => {
      const dims = naturalDims[upc]
      const aspectRatio =
        dims && dims.h > 0 ? dims.w / dims.h : FALLBACK_ASPECT_RATIO

      return Math.max(productHeight * 0.3, productHeight * aspectRatio)
    },
    [naturalDims],
  )

  const naturalShelfMetrics = useMemo(
    () =>
      products.map(shelf => {
        const facings = shelf.map(facing => {
          const height = getBaseProductHeight(
            getVolumeOz(facing.packaging?.size, facing.packaging?.unit),
          )
          const width = getBaseProductWidth(facing.upcA, height)
          return { height, width }
        })

        const width = facings.reduce(
          (total, facing, index) =>
            total + facing.width + (index > 0 ? GAP_PX : 0),
          0,
        )
        const height =
          facings.reduce((max, facing) => Math.max(max, facing.height), 0) ||
          shelfHeight

        return { width, height }
      }),
    [getBaseProductHeight, getBaseProductWidth, products, shelfHeight],
  )

  const naturalPlanogramWidth = useMemo(() => {
    const widestShelf = naturalShelfMetrics.length
      ? Math.max(...naturalShelfMetrics.map(shelf => shelf.width))
      : 0

    return spacingPx.outerPadding * 2 + spacingPx.innerPadding * 2 + widestShelf
  }, [naturalShelfMetrics, spacingPx.innerPadding, spacingPx.outerPadding])

  const naturalPlanogramHeight = useMemo(() => {
    const shelvesHeight = naturalShelfMetrics.reduce(
      (total, shelf) => total + shelf.height + spacingPx.shelfLine,
      0,
    )

    return (
      spacingPx.outerPadding * 2 +
      spacingPx.innerPadding * 2 +
      spacingPx.shelfGap * Math.max(products.length - 1, 0) +
      shelvesHeight
    )
  }, [
    naturalShelfMetrics,
    products.length,
    spacingPx.innerPadding,
    spacingPx.outerPadding,
    spacingPx.shelfGap,
    spacingPx.shelfLine,
  ])

  const widthScale =
    containerWidth && naturalPlanogramWidth
      ? containerWidth / naturalPlanogramWidth
      : 1
  const heightScale =
    maxHeight && naturalPlanogramHeight ? maxHeight / naturalPlanogramHeight : 1
  const planogramScale = maxHeight
    ? Math.min(widthScale, heightScale)
    : widthScale

  return (
    <>
      <Box
        ref={availableWidthRef}
        sx={{
          width: '100%',
          maxHeight: maxHeight || 'none',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: naturalPlanogramWidth * planogramScale,
            height: naturalPlanogramHeight * planogramScale,
            position: 'relative',
            flex: '0 0 auto',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: naturalPlanogramWidth,
              height: naturalPlanogramHeight,
              transform: `scale(${planogramScale})`,
              transformOrigin: 'top left',
              backgroundColor: '#000',
              borderRadius: `${spacingPx.frameRadius}px`,
              padding: `${spacingPx.outerPadding}px`,
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#fff',
                padding: `${spacingPx.innerPadding}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: `${spacingPx.shelfGap}px`,
                boxSizing: 'border-box',
              }}
            >
              {products.map((shelf, shelfIndex) => (
                <Box key={`shelf-${shelfIndex}`} sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-evenly',
                      gap: `${GAP_PX}px`,
                      height:
                        naturalShelfMetrics[shelfIndex]?.height || shelfHeight,
                      width: '100%',
                    }}
                  >
                    {(reversed ? [...shelf].reverse() : shelf).map(
                      (facing, facingIndex) => {
                        const positionKey = `${shelfIndex}-${facingIndex}`
                        const highlight =
                          highlightByPosition?.[positionKey] ??
                          highlightByUpc?.[facing.upcA]
                        const popoverEnabled =
                          !popoverEnabledByPosition ||
                          Boolean(popoverEnabledByPosition[positionKey])
                        const isLoadingImage = Boolean(
                          loadingByUpc?.[facing.upcA],
                        )
                        const imageStyle = imageStyleByUpc?.[facing.upcA]
                        const uniformScale =
                          imageStyle?.scale ??
                          imageStyle?.scaleX ??
                          imageStyle?.scaleY ??
                          1
                        const productHeight = getBaseProductHeight(
                          getVolumeOz(
                            facing.packaging?.size,
                            facing.packaging?.unit,
                          ),
                        )
                        const productWidth = getBaseProductWidth(
                          facing.upcA,
                          productHeight,
                        )

                        return (
                          <Box
                            title={facingTitleByPosition?.[positionKey]}
                            key={`facing-${facingIndex}-shelf-${shelfIndex}`}
                            sx={{
                              position: 'relative',
                              display: 'flex',
                              flexDirection: 'row',
                              flex: '0 0 auto',
                              height: productHeight,
                              width: productWidth,
                              minWidth: productWidth,
                              borderRadius: 0.5,
                              overflow: 'hidden',
                              cursor:
                                isMobile && popoverEnabled
                                  ? 'pointer'
                                  : 'default',
                            }}
                            onClick={() => {
                              if (isMobile && popoverEnabled) {
                                onFacingHover?.(facing.upcA)
                                setMobileActivePositionKey(positionKey)
                                setMobileActiveUpc(facing.upcA)
                              }
                            }}
                            onDoubleClick={
                              isMobile || !popoverEnabled
                                ? undefined
                                : event => {
                                    setActivePositionKey(positionKey)
                                    handleBoxClick(event, facing.upcA)
                                  }
                            }
                            onMouseEnter={
                              isMobile || !popoverEnabled
                                ? undefined
                                : event => {
                                    setActivePositionKey(positionKey)
                                    onFacingHover?.(facing.upcA)
                                    handleBoxMouseEnter(event, facing.upcA)
                                  }
                            }
                            onMouseLeave={
                              isMobile || !popoverEnabled
                                ? undefined
                                : () => {
                                    onFacingLeave?.()
                                    handleBoxMouseLeave()
                                  }
                            }
                          >
                            <img
                              onLoad={e => {
                                const img = e.currentTarget
                                setNaturalDims(prev => ({
                                  ...prev,
                                  [facing.upcA]: {
                                    w: img.naturalWidth,
                                    h: img.naturalHeight,
                                  },
                                }))
                              }}
                              src={
                                facing.planogramImageUrl ||
                                facing.marketingImageUrl ||
                                '/static/img/products/unknown-product-planogram-facing.png'
                              }
                              style={{
                                height: '100%',
                                width: '100%',
                                objectFit: imageFit,
                                objectPosition:
                                  imageStyle?.objectPosition || 'bottom',
                                transform: `scale(${uniformScale})`,
                                transformOrigin: 'center center',
                                opacity: isLoadingImage ? 0.55 : 1,
                                display: 'block',
                              }}
                            />
                            {!!highlight?.bgColor && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  backgroundColor: highlight.bgColor,
                                  border: highlight.borderColor
                                    ? `2px solid ${highlight.borderColor}`
                                    : 'none',
                                  pointerEvents: 'none',
                                  zIndex: 1,
                                }}
                              />
                            )}
                            {isLoadingImage && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  pointerEvents: 'none',
                                  zIndex: 2,
                                }}
                              >
                                <CircularProgress size={16} />
                              </Box>
                            )}
                          </Box>
                        )
                      },
                    )}
                  </Box>
                  <Box
                    sx={{
                      height: `${spacingPx.shelfLine}px`,
                      width: '100%',
                      border: '1px solid #000',
                      boxSizing: 'border-box',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <StyledPopper
        id="product-menu"
        anchorEl={anchorEl}
        open={!isMobile && open}
        placement={popperPlacement}
        {...productPopperProps}
        modifiers={getProductPopperModifiers(arrowRef)}
      >
        {activeUpc && (
          <>
            <Box
              component="span"
              className="arrow"
              ref={setArrowRef}
              sx={styles.arrow}
            />
            {renderPopoverContent ? (
              <Box
                sx={productPopoverContentSx}
                onMouseEnter={handlePopperMouseEnter}
                onMouseLeave={handlePopperMouseLeave}
              >
                {renderPopoverContent(activeUpc, activePositionKey)}
              </Box>
            ) : (
              <Box
                sx={productPopoverContentSx}
                onMouseEnter={handlePopperMouseEnter}
                onMouseLeave={handlePopperMouseLeave}
              >
                <ProductDetailsCard
                  upc={activeUpc}
                  hideDetails
                  preferredNameOverride={preferredNameByUpc[activeUpc]}
                  initialInfo={productInfoByUpc?.[activeUpc]}
                />
              </Box>
            )}
          </>
        )}
      </StyledPopper>
      <Drawer
        anchor="bottom"
        open={isMobile && Boolean(mobileActiveUpc)}
        onClose={() => {
          setMobileActiveUpc(null)
          setMobileActivePositionKey(undefined)
        }}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          zIndex: theme => theme.zIndex.modal + 2,
        }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh',
            zIndex: theme => theme.zIndex.modal + 3,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {t('renderedPlanogram.drawer.title')}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setMobileActiveUpc(null)
                setMobileActivePositionKey(undefined)
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {drawerContent}
          </Box>
        </Box>
      </Drawer>
    </>
  )
}

export default RenderedPlanogram
